/**
 * Zerlegt eine Dateiauswahl in Häppchen, die das Azure-Frontend durchlässt.
 *
 * **Warum das nötig ist:** ein einzelner Multipart-Request mit deutlich mehr Dateien wird vor dem
 * Backend abgewiesen (Request-Größe), taucht in Application Insights gar nicht erst auf, und der
 * Client sieht davon nur einen generischen Fehler. In PROD ist nie ein Batch über 30 Dateien
 * angekommen. Die Zahlen unten sind dort erlitten und keine Schätzung.
 *
 * Herkunft: der Produktbild-Massenupload der internen Webapp. Beim zweiten Aufrufer (Eingang für
 * Absetzungen und Abrechnungen) hierher gezogen, damit die Grenzen nicht in zwei Kopien
 * auseinanderlaufen. Hintergrund im Backend-Repo: `docs/claude/storage-und-caching.md`.
 */
export const MAX_FILES_PER_BATCH = 10

export const MAX_BYTES_PER_BATCH = 8 * 1024 * 1024

export interface BatchGrenzen {
  maxFiles: number
  maxBytes: number
}

export const STANDARD_BATCH_GRENZEN: BatchGrenzen = {
  maxFiles: MAX_FILES_PER_BATCH,
  maxBytes: MAX_BYTES_PER_BATCH,
}

/**
 * Füllt Batches, bis entweder die Anzahl oder die Bytes erreicht sind.
 *
 * Eine einzelne Datei über dem Byte-Limit bildet ihren eigenen Batch: sie abzuweisen wäre hier die
 * falsche Stelle, das entscheidet der Aufrufer anhand seiner eigenen Obergrenze.
 */
export function toBatches(files: File[], grenzen: BatchGrenzen = STANDARD_BATCH_GRENZEN): File[][] {
  const batches: File[][] = []
  let current: File[] = []
  let currentBytes = 0

  for (const file of files) {
    const wouldExceed = current.length >= grenzen.maxFiles || currentBytes + file.size > grenzen.maxBytes
    if (current.length > 0 && wouldExceed) {
      batches.push(current)
      current = []
      currentBytes = 0
    }
    current.push(file)
    currentBytes += file.size
  }

  if (current.length > 0) batches.push(current)
  return batches
}

/** Ein Upload-Vorgang: was ein Mensch mit einem Klick losgeschickt hat. */
export interface UploadGruppe<T> {
  batchId: string
  eintraege: T[]
}

/**
 * Gruppiert abgelegte Dateien nach ihrem Upload-Vorgang, jüngster zuerst.
 *
 * Die Gegenrichtung zu {@link toBatches}: dort wird EIN Vorgang in mehrere Requests zerlegt, hier
 * werden die Zeilen wieder zu dem Vorgang zusammengefasst, als den der Nutzer sie kennt. Das trägt
 * nur, weil die `uploadBatchId` vom Client kommt und in allen Häppchen dieselbe ist — vergäbe sie
 * das Backend je Request, entstünden hier mehrere Gruppen aus einem Klick.
 *
 * Innerhalb einer Gruppe bleibt die Reihenfolge, in der die Einträge hereinkamen (das Backend
 * sortiert bereits nach Zeitpunkt).
 */
export function nachUploadGruppieren<T extends { uploadBatchId: string; hochgeladenAm: string }>(
  eintraege: readonly T[],
): UploadGruppe<T>[] {
  const gruppen = new Map<string, T[]>()
  for (const eintrag of eintraege) {
    const bestand = gruppen.get(eintrag.uploadBatchId)
    if (bestand) bestand.push(eintrag)
    else gruppen.set(eintrag.uploadBatchId, [eintrag])
  }

  return [...gruppen.entries()]
    .map(([batchId, gruppe]) => ({ batchId, eintraege: gruppe }))
    .sort((a, b) => juengster(b.eintraege).localeCompare(juengster(a.eintraege)))
}

/**
 * Ob eine abgelegte Datei im Browser angezeigt werden kann (PDF oder Bild) oder nur heruntergeladen.
 *
 * Seit der Eingang für Absetzungen jeden Dateityp annimmt (ZIP, CSV aus den Kassenportalen), liefert
 * das Backend alles außer PDF, JPEG und PNG als `application/octet-stream` aus. „Ansehen“ bräuchte
 * dafür einen neuen Tab, der nur einen Download auslöst. Deshalb blenden beide Oberflächen den Knopf
 * dort aus.
 */
export function imBrowserAnzeigbar(contentType: string | undefined): boolean {
  return contentType === 'application/pdf' || contentType === 'application/x-pdf' || !!contentType?.startsWith('image/')
}

/** ISO-8601 sortiert lexikografisch wie chronologisch, deshalb reicht der String-Vergleich. */
function juengster<T extends { hochgeladenAm: string }>(eintraege: T[]): string {
  return eintraege.reduce((max, e) => (e.hochgeladenAm > max ? e.hochgeladenAm : max), eintraege[0].hochgeladenAm)
}
